// ENG-6661: conversation eval-FAILURE aggregation for the platform-admin
// Dashboard + the admin-debug report.
//
// Companion to eval-scores.ts. Where that answers "are conversations
// succeeding?" from evaluated rows, this answers "which conversations could the
// host NOT score, and why?" from rows carrying a terminal eval_failure_reason.
// Pure, DB-agnostic, browser-safe (no node:* imports) so both the Hono API and
// the Next.js webapp can share it, exactly like the other conversations helpers.
//
// MODEL:
//   - A row enters here only once the host evaluator has given up reconstructing
//     it for a known, data-shaped reason (no_transcript / not_reconstructable /
//     empty_transcript). Transient backend/transport failures are NOT here - they
//     don't stamp a conversation (they retry) and are reported at host grain on
//     hosts.eval_backend_* instead.
//   - Failures are windowed on `last_message_at` (the conversation's own clock;
//     these rows are un-evaluated so evaluated_at is NULL).
//   - "Gave up" = eval_attempts has reached the retry cap: the failure is
//     terminal, the conversation will never be scored. Rows below the cap are
//     still being retried and may yet succeed.

import type { ConversationMetricsPeriod } from './metrics.js';

/**
 * Closed set of data-shaped, budget-consuming skip reasons. SINGLE SOURCE OF
 * TRUTH - the DB CHECK (20260617000007), the API write-route validation, and the
 * host evaluator all agree with this list.
 *   no_transcript       - no local transcript turns at all for the agent.
 *   not_reconstructable - turns exist but none carry the conversation's channel_ref.
 *   empty_transcript    - turns reconstruct but render to nothing.
 */
export const EVAL_FAILURE_REASONS = [
  'no_transcript',
  'not_reconstructable',
  'empty_transcript',
] as const;

export type EvalFailureReason = (typeof EVAL_FAILURE_REASONS)[number];

/** Type guard for an untrusted wire value. */
export function isEvalFailureReason(v: unknown): v is EvalFailureReason {
  return typeof v === 'string' && (EVAL_FAILURE_REASONS as readonly string[]).includes(v);
}

/**
 * ENG-7137: TERMINAL exclusion reasons - the host evaluator recognised this
 * conversation as something that should never have been scored at all, so it is
 * dropped in ONE shot (eval_attempts jumped straight to the cap) rather than
 * retried. These are deliberately NOT in EVAL_FAILURE_REASONS: an exclusion is
 * not an evaluator failure, so the eval-FAILURE aggregator above ignores it (it
 * only counts known EVAL_FAILURE_REASONS), and these rows simply fall out of the
 * scored denominator without inflating the "unevaluable" failure breakdown.
 *   synthetic_probe - a synthetic liveness probe that leaked past ingest
 *                     classification (missing/!=end_user sender_class).
 *   ack_only        - a conversation whose only agent turn is a bare "ack"
 *                     (e.g. a probe acknowledgement), i.e. no real exchange to score.
 * Stored in the same eval_failure_reason column (the DB CHECK allows them), so
 * the give-up reason stays explainable on the reporting surface.
 */
export const EVAL_EXCLUSION_REASONS = ['synthetic_probe', 'ack_only'] as const;

export type EvalExclusionReason = (typeof EVAL_EXCLUSION_REASONS)[number];

/** Type guard for an untrusted wire value. */
export function isEvalExclusionReason(v: unknown): v is EvalExclusionReason {
  return typeof v === 'string' && (EVAL_EXCLUSION_REASONS as readonly string[]).includes(v);
}

/**
 * The conversation-eval retry cap. Mirrors MAX_CONVERSATION_EVAL_ATTEMPTS in
 * packages/api/src/routes/host-runtime.ts and the conversations_eval_attempts_check
 * CHECK - a row is handed out while attempts < cap, so reaching the cap is
 * terminal ("gave up").
 */
export const EVAL_ATTEMPTS_CAP = 3;

/** Window length per period (mirrors conversation metrics; failures need no MA). */
const PERIOD_WINDOW_MS: Record<ConversationMetricsPeriod, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

/**
 * One un-scored conversation carrying a terminal failure reason. The caller
 * filters to `eval_failure_reason IS NOT NULL` and fetches the display window;
 * code_name/display_name/org_name ride along so the helper needs no agent lookup.
 */
export interface ConversationEvalFailureRow {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  org_name: string | null;
  eval_failure_reason: string;
  eval_attempts: number;
  last_message_at: string;
}

/** Org-wide count for one reason, with the terminal ("gave up") subset broken out. */
export interface EvalFailureReasonCount {
  reason: EvalFailureReason;
  /** Conversations currently carrying this reason in the window. */
  count: number;
  /** Subset that have reached the retry cap (will never be scored). */
  gaveUp: number;
}

/** Per-agent failure standing for the ranked table. */
export interface AgentEvalFailureSummary {
  agentId: string;
  codeName: string;
  displayName: string | null;
  orgName: string | null;
  /** Total failing conversations for this agent in the window. */
  total: number;
  /** Subset that have given up (terminal). */
  gaveUp: number;
  /** Count per reason (every reason key present, zero-filled). */
  byReason: Record<EvalFailureReason, number>;
}

export interface ConversationEvalFailuresResult {
  period: ConversationMetricsPeriod;
  period_start: string;
  /** Total failing conversations across all agents in the window. */
  total: number;
  /** Subset that have given up (terminal). */
  gaveUp: number;
  /** Org-wide breakdown, ordered by count desc (every reason present, zero-filled). */
  byReason: EvalFailureReasonCount[];
  /** Per-agent standing, ranked worst-first (most failures). */
  perAgent: AgentEvalFailureSummary[];
}

function zeroReasonMap(): Record<EvalFailureReason, number> {
  return EVAL_FAILURE_REASONS.reduce(
    (acc, r) => {
      acc[r] = 0;
      return acc;
    },
    {} as Record<EvalFailureReason, number>,
  );
}

/**
 * Aggregate failing conversation rows into an org-wide reason breakdown plus a
 * per-agent standing table. `now` is injectable for deterministic tests. Rows
 * outside the display window, or with an unknown reason, are ignored defensively.
 */
export function aggregateConversationEvalFailures(
  rows: ConversationEvalFailureRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
  attemptsCap: number = EVAL_ATTEMPTS_CAP,
): ConversationEvalFailuresResult {
  const windowMs = PERIOD_WINDOW_MS[period];
  const nowMs = now.getTime();
  const periodStart = new Date(nowMs - windowMs);
  const periodStartMs = periodStart.getTime();

  const orgByReason = zeroReasonMap();
  const orgGaveUpByReason = zeroReasonMap();
  let total = 0;
  let gaveUp = 0;

  const byAgent = new Map<string, AgentEvalFailureSummary & { _gaveUpByReason: Record<EvalFailureReason, number> }>();

  for (const row of rows) {
    if (!isEvalFailureReason(row.eval_failure_reason)) continue; // ignore unknown reasons
    const ts = new Date(row.last_message_at).getTime();
    if (!Number.isFinite(ts) || ts < periodStartMs || ts > nowMs) continue;

    const reason = row.eval_failure_reason;
    const isTerminal = Number.isFinite(row.eval_attempts) && row.eval_attempts >= attemptsCap;

    total += 1;
    orgByReason[reason] += 1;
    if (isTerminal) {
      gaveUp += 1;
      orgGaveUpByReason[reason] += 1;
    }

    let entry = byAgent.get(row.agent_id);
    if (!entry) {
      entry = {
        agentId: row.agent_id,
        codeName: row.code_name,
        displayName: row.display_name,
        orgName: row.org_name,
        total: 0,
        gaveUp: 0,
        byReason: zeroReasonMap(),
        _gaveUpByReason: zeroReasonMap(),
      };
      byAgent.set(row.agent_id, entry);
    }
    entry.total += 1;
    entry.byReason[reason] += 1;
    if (isTerminal) {
      entry.gaveUp += 1;
      entry._gaveUpByReason[reason] += 1;
    }
  }

  const byReason: EvalFailureReasonCount[] = EVAL_FAILURE_REASONS.map((reason) => ({
    reason,
    count: orgByReason[reason],
    gaveUp: orgGaveUpByReason[reason],
  })).sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  const perAgent: AgentEvalFailureSummary[] = [...byAgent.values()]
    .map(({ _gaveUpByReason, ...summary }) => {
      void _gaveUpByReason; // retained per-agent terminal split is not surfaced today
      return summary;
    })
    .sort(
      (a, b) =>
        b.total - a.total || b.gaveUp - a.gaveUp || a.codeName.localeCompare(b.codeName),
    );

  return {
    period,
    period_start: periodStart.toISOString(),
    total,
    gaveUp,
    byReason,
    perAgent,
  };
}
